import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  discontinueBranchOfficeArticle,
  getBranchOfficesFiltered,
  getBrandsByBranchOfficeId,
  getCategoriesByBrandAndBranchOfficeId,
  getDepositsFiltered,
  getSkusByBrandCategoryAndBranchOfficeId,
  getArticlesByBranchOfficeOrDepositAndBrandCategoryCatalogue,
} from "../../../api/Services";
import { BusinessUniteContext } from "../../../components/layout/Layout";
import {
  hasPermission,
  privilegesEnum,
} from "../../../helpers/PermissionHelper";
import { getOrganizationId } from "../../../api/Helper";

import {
  Button,
  Cascader,
  Dropdown,
  Menu,
  message,
  Select,
  Spin,
  Table,
} from "antd";
import { MoreOutlined, RedoOutlined, SendOutlined } from "@ant-design/icons";

import styles from "./Catalogue.module.scss";
import { useNavigate } from "react-router-dom";
import hardcodedData from "./hardcodedData.json";

enum filterSkus {
  all = "all",
  active = "active",
  inactive = "inactive",
}

const HARDCODED_ORG_ID = 5;
const HARDCODED_BRANCH_OFFICE_ID = 1;

const Catalogue = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState<any[]>([]);
  const [loadingDeposits, setLoadingDeposits] = useState(false);
  const [loadingBranchOffices, setLoadingBranchOffices] = useState(false);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(0);
  const [actualFilterSkus, setActualFilterSkus] = useState(filterSkus.all);
  const [actualBranchOfficeId, setActualBranchOfficeId] = useState<any>(null);
  const [actualFilter, setActualFilter] = useState<
    "deposit" | "branchoffice" | null
  >(null);
  const [actualDepositId, setActualDepositId] = useState<any>(null);
  const [branchOfficeOptions, setBranchOfficeOptions] = useState([]);
  const [depositOptions, setDepositOptions] = useState([]);
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [expandedRowData, setExpandedRowData] = useState<{
    [key: string]: any[];
  }>({});
  const [loadingExpandedRow, setLoadingExpandedRow] = useState<{
    [key: string]: boolean;
  }>({});
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [expandedCategoryData, setExpandedCategoryData] = useState<{
    [key: string]: any[];
  }>({});
  const [loadingCategoryRow, setLoadingCategoryRow] = useState<{
    [key: string]: boolean;
  }>({});

  const { contextValue: businessUnitSelected } = useContext(BusinessUniteContext);
  const [organizationId, setOrganizationId] = useState<number | null>(null);

  useEffect(() => {
    handleSearchBranchOffices("");
    handleSearchDeposits("");
    const fetchOrg = async () => {
      const orgId = await getOrganizationId();
      setOrganizationId(Number(orgId));
    };
    fetchOrg();
  }, []);

  const handlePagination = (page: number) => {
    handleFetchArticles(actualBranchOfficeId, page);
    setActualPage(page);
  };

  const handleFetchArticles = async (
    branchOfficeId?: number,
    depositId?: number,
    filter = actualFilter,
    pagination = actualPage
  ) => {
    if (
      Number(organizationId) === HARDCODED_ORG_ID &&
      Number(branchOfficeId) === HARDCODED_BRANCH_OFFICE_ID
    ) {
      console.log("hardcodedData.brands", hardcodedData.brands);
      setData(hardcodedData.brands);
      setTotalSize(hardcodedData.brands.length);
      setLoadingArticles(false);
      return;
    }
    if (!branchOfficeId && !depositId) {
      message.error(t("Common-SelectBranchOfficeError"));
      return;
    }
    setData([]);
    setLoadingArticles(true);
    const response = await getBrandsByBranchOfficeId(
      {
        branchOfficeId,
        depositId,
      },
      pagination
    );
    setData(response.brands);
    setTotalSize(response.totalElements);
    setLoadingArticles(false);
  };

  const handleSearchBranchOffices = async (value: string) => {
    if (businessUnitSelected) {
      setLoadingBranchOffices(true);
      const response = await getBranchOfficesFiltered(0, {
        name: value,
        businessUnitId: businessUnitSelected,
      });
      const options = response.branchOffices.map((item: any) => ({
        value: item.id,
        text: item.name,
      }));
      setBranchOfficeOptions(options);
      setLoadingBranchOffices(false);
    } else setBranchOfficeOptions([]);
  };

  const handleSearchDeposits = async (value: string) => {
    setLoadingDeposits(true);
    const response = await getDepositsFiltered(0, {
      name: value,
    });
    const options = response.deposits.map((item: any) => ({
      value: item.id,
      text: item.name,
    }));
    setDepositOptions(options);
    setLoadingDeposits(false);
  };

  const getRowClassName = (_: any, index: number) => {
    return index % 2 === 0 ? styles.rowLight : styles.rowDark;
  };

  const handleUnsupplyArticle = async (sku: any) => {
    const deleteStatus = await discontinueBranchOfficeArticle(
      actualBranchOfficeId,
      sku.id
    );
    if (!deleteStatus) message.error(t("Common-ErrorMessage"));
    else message.success(t("Common-SuccessMessage"));
  };

  const columnsBrands = [
    {
      title: t("Common-Brand"),
      key: "name",
      render: (record: any) => record.brand?.name,
    },
    {
      title: t("Common-Stock"),
      dataIndex: "totalStock",
    },
    {
      title: t("Common-Supply"),
      key: "unsupply",
      render: (record: any) => record.unsupply ?? '',
    },
    {
      title: t("Common-Analysis"),
      key: "analysis",
      render: (record: any) => record.analysis ?? '',
    },
    {
      title: t("Common-Strategy"),
      key: "strategy",
      render: (record: any) => record.strategy ?? '',
    },
    {
      title: t("Common-InventoryLevelOptimal"),
      key: "inventory",
      render: (record: any) => record.inventory ?? '',
    },
  ];

  const columnsCategories = [
    {
      title: t("Common-Category"),
      key: "name",
      render: (category: any) => {
        const parentsString = category?.parents
          ? category.parents.filter(Boolean).join(" - ")
          : "";
        const categoryName = category?.categoryName || "";

        return parentsString
          ? `${parentsString} - ${categoryName}`
          : categoryName;
      },
    },
    {
      title: t("Common-Stock"),
      dataIndex: "totalStock",
    },
    {
      title: t("Common-Supply"),
      key: "unsupply",
      render: (record: any) => record.unsupply ?? '',
    },
    {
      title: t("Common-Analysis"),
      key: "analysis",
      render: (record: any) => record.analysis ?? '',
    },
    {
      title: t("Common-Strategy"),
      key: "strategy",
      render: (record: any) => record.strategy ?? '',
    },
    {
      title: t("Common-InventoryLevelOptimal"),
      key: "inventory",
      render: (record: any) => record.inventory ?? '',
    },
  ];

  const columnsArticles = [
    {
      title: t("Common-Article"),
      key: "article",
      render: (article: any) => (
        <a href={`/admin/articles/${article?.id || ""}/view`}>
          {article?.name || "-"}
        </a>
      ),
    },
    {
      title: t("Common-Stock"),
      dataIndex: "totalStockArticles",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Supply"),
      dataIndex: "unsupply",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Analysis"),
      dataIndex: "analysis",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-Strategy"),
      dataIndex: "strategy",
      render: (value: any) => value || "-",
    },
    {
      title: t("Common-InventoryLevelOptimal"),
      dataIndex: "inventory",
      render: (value: any) => value || "-",
    },
    {
      key: "actions",
      render: (record: any) => (
        <Dropdown
          overlay={
            <Menu>
              {hasPermission(privilegesEnum.WRITE_REPOSITION_ORDERS) && (
                <Menu.Item
                  key="edit"
                  onClick={() => {
                    navigate("/repositionorders/create", {
                      state: {
                        articleId: record.id,
                        articleName: record.name,
                      },
                    });
                  }}
                  icon={<SendOutlined />}
                >
                  {t("Common-AddRepositionOrder")}
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <Button
            onClick={(e) => e.preventDefault()}
            className={styles.actionButtonMore}
          >
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  const handleFetchCategories = async (brandId: number | string) => {
    if (
      Number(organizationId) === HARDCODED_ORG_ID &&
      Number(actualBranchOfficeId) === HARDCODED_BRANCH_OFFICE_ID
    ) {
      const brand = hardcodedData.brands.find((b) => String(b.brand.id) === String(brandId));
      setExpandedRowData((prev) => ({ ...prev, [brandId]: brand ? brand.categories : [] }));
      setLoadingExpandedRow((prev) => ({ ...prev, [brandId]: false }));
      return;
    }
    setLoadingExpandedRow((prev) => ({ ...prev, [brandId]: true }));
    try {
      if (!(Number(organizationId) === HARDCODED_ORG_ID && Number(actualBranchOfficeId) === HARDCODED_BRANCH_OFFICE_ID)) {
        const response = await getCategoriesByBrandAndBranchOfficeId(
          { branchOfficeId: actualBranchOfficeId, depositId: actualDepositId },
          Number(brandId),
          0
        );
        setExpandedRowData((prev) => ({
          ...prev,
          [brandId]: response.categories,
        }));
      }
      setLoadingExpandedRow((prev) => ({ ...prev, [brandId]: false }));
    } catch (error) {
      setLoadingExpandedRow((prev) => ({ ...prev, [brandId]: false }));
    }
  };

  const groupSkusByArticle = (skus: any[]) => {
    const articleMap = new Map();
    
    skus.forEach((sku) => {
      const articleId = sku.articleId;
      const articleKey = `${articleId}-${sku.articleName}`;
      
      if (!articleMap.has(articleKey)) {
        articleMap.set(articleKey, {
          articleId: sku.articleId,
          articleName: sku.articleName,
          totalStockArticles: 0,
          unsupply: sku.unsupply ?? "-",
          analysis: sku.analysis ?? "-",
          strategy: sku.strategy ?? "-",
          inventory: sku.inventory ?? "-",
          skus: []
        });
      }
      
      const article = articleMap.get(articleKey);
      article.totalStockArticles += sku.stock || 0;
      article.skus.push(sku);
    });
    
    return Array.from(articleMap.values());
  };

  const handleFetchArticlesByCategory = useCallback(
    async (brandId: number | string, categoryId: number | string) => {
      if (
        Number(organizationId) === HARDCODED_ORG_ID &&
        Number(actualBranchOfficeId) === HARDCODED_BRANCH_OFFICE_ID
      ) {
        const brand = hardcodedData.brands.find((b) => String(b.brand.id) === String(brandId));
        const category = brand?.categories.find((c) => String(c.categoryId) === String(categoryId));
        const skus = category ? category.skus : [];
        const articles = groupSkusByArticle(skus);
        setExpandedCategoryData((prev) => ({ ...prev, [categoryId]: articles }));
        setLoadingCategoryRow((prev) => ({ ...prev, [categoryId]: false }));
        return;
      }
      setLoadingCategoryRow((prev) => ({ ...prev, [categoryId]: true }));
      try {
        if (!(Number(organizationId) === HARDCODED_ORG_ID && Number(actualBranchOfficeId) === HARDCODED_BRANCH_OFFICE_ID)) {
          const response = await getArticlesByBranchOfficeOrDepositAndBrandCategoryCatalogue(
            { branchOfficeId: actualBranchOfficeId, depositId: actualDepositId },
            Number(brandId),
            Number(categoryId),
            0,
            actualFilterSkus === filterSkus.active
              ? 0
              : actualFilterSkus === filterSkus.inactive
              ? 1
              : undefined
          );
          setExpandedCategoryData((prev) => ({
            ...prev,
            [categoryId]: response.articles,
          }));
        }
        setLoadingCategoryRow((prev) => ({ ...prev, [categoryId]: false }));
      } catch (error) {
        setLoadingCategoryRow((prev) => ({ ...prev, [categoryId]: false }));
      }
    },
    [actualFilterSkus, actualBranchOfficeId, actualDepositId]
  );

  const expandedCategoryRender = (brandId: string) => (record: any) => {
    let articles = expandedCategoryData[record.categoryId];
    if (
      Number(organizationId) === HARDCODED_ORG_ID &&
      Number(actualBranchOfficeId) === HARDCODED_BRANCH_OFFICE_ID
    ) {
      if (!articles) {
        const brand = hardcodedData.brands.find((b) => String(b.brand.id) === String(brandId));
        const category = brand?.categories.find((c) => String(c.categoryId) === String(record.categoryId));
        const skus = category ? category.skus : [];
        articles = groupSkusByArticle(skus);
      }
    }
    const isLoading = loadingCategoryRow[record.categoryId];
    if (isLoading) {
      return <Spin />;
    }
    return (
      <Table
        columns={columnsArticles}
        dataSource={articles || []}
        pagination={false}
        rowKey={(record) => String(record.id)}
      />
    );
  };

  const expandedRowRender = (record: any) => {
    let categories = expandedRowData[record.brand.id];
    if (
      Number(organizationId) === HARDCODED_ORG_ID &&
      Number(actualBranchOfficeId) === HARDCODED_BRANCH_OFFICE_ID
    ) {
      if (!categories) {
        const brand = hardcodedData.brands.find((b) => String(b.brand.id) === String(record.brand.id));
        categories = brand ? brand.categories : [];
      }
    }
    const isLoading = loadingExpandedRow[record.brand.id];
    if (isLoading) {
      return <Spin />;
    }
    return (
      <Table
        columns={columnsCategories}
        dataSource={categories || []}
        pagination={false}
        rowKey={(record) => String(record.categoryId)}
        expandedRowRender={expandedCategoryRender(record.brand.id)}
        onExpand={(expanded, categoryRecord) => {
          if (expanded) {
            handleFetchArticlesByCategory(String(record.brand.id), String(categoryRecord.categoryId));
            setExpandedCategories([
              ...expandedCategories,
              String(categoryRecord.categoryId),
            ]);
          } else {
            setExpandedCategories(
              expandedCategories.filter(
                (id) => id !== String(categoryRecord.categoryId)
              )
            );
          }
        }}
        expandedRowKeys={expandedCategories}
      />
    );
  };

  const handleCascaderChange = (value: any) => {
    if (value) {
      if (value[0] === "BranchOffice") {
        const selectedBranchOfficeId = value[1];
        handleFetchArticles(selectedBranchOfficeId, undefined);
        setActualBranchOfficeId(selectedBranchOfficeId);
        setActualDepositId(undefined);
        setActualFilter("branchoffice");
      } else if (value[0] === "Deposit") {
        const selectedDepositId = value[1];
        handleFetchArticles(undefined, selectedDepositId);
        setActualBranchOfficeId(undefined);
        setActualDepositId(selectedDepositId);
        setActualFilter("deposit");
      }
    }
  };

  const handleSearch = async (inputValue: string) => {
    if (inputValue.length >= 3) {
      await handleSearchBranchOffices(inputValue);
      await handleSearchDeposits(inputValue);
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t("Common-Catalogue")}</h1>
      </div>
      <div className={styles.contentWrapper}>
        {hasPermission(privilegesEnum.READ_REPOSITION_ORDERS) && (
          <div className={styles.tableWrapper}>
            <div className={styles.headerTableWrapper}>
              <div className={styles.filtersWrapper}>
                <div className={styles.filterWrapper}>
                  <Cascader
                    placeholder={t(
                      "Common-SearchBranchOfficeDepositTextPlaceholder"
                    )}
                    onSearch={handleSearch}
                    className={styles.filterSelect}
                    onChange={handleCascaderChange}
                    showSearch
                    options={[
                      {
                        label: t("Common-BranchOffice"),
                        value: "BranchOffice",
                        children: (branchOfficeOptions || []).map((d: any) => ({
                          value: d.value,
                          label: d.text,
                        })),
                        disabled: branchOfficeOptions.length === 0,
                      },
                      {
                        label: t("Common-Deposit"),
                        value: "Deposit",
                        children: (depositOptions || []).map((d: any) => ({
                          value: d.value,
                          label: d.text,
                        })),
                        disabled: depositOptions.length === 0,
                      },
                    ]}
                  />
                </div>
                <div className={styles.filterWrapper}>
                  <Select
                    className={styles.filterSelect}
                    allowClear
                    defaultValue={actualFilterSkus}
                    onChange={(value) => {
                      setExpandedCategories([]);
                      setActualFilterSkus(value);
                    }}
                  >
                    <Select.Option value={filterSkus.all} key={filterSkus.all}>
                      {t("Common-All")}
                    </Select.Option>
                    <Select.Option
                      value={filterSkus.active}
                      key={filterSkus.active}
                    >
                      {t("Common-Actives")}
                    </Select.Option>
                    <Select.Option
                      value={filterSkus.inactive}
                      key={filterSkus.inactive}
                    >
                      {t("Common-Inactives")}
                    </Select.Option>
                  </Select>
                </div>
              </div>
              <Button
                icon={<RedoOutlined />}
                onClick={() =>
                  handleFetchArticles(actualBranchOfficeId, actualDepositId)
                }
              />
            </div>
            <Table
              size="small"
              className={styles.table}
              loading={loadingArticles}
              columns={columnsBrands}
              expandedRowRender={expandedRowRender}
              onExpand={(expanded, record) => {
                if (expanded) {
                  handleFetchCategories(String(record.brand.id));
                  setExpandedRows([...expandedRows, String(record.brand.id)]);
                } else {
                  setExpandedRows(
                    expandedRows.filter((id) => id !== String(record.brand.id))
                  );
                }
              }}
              expandedRowKeys={expandedRows}
              dataSource={data}
              rowKey={(record) => String(record.brand.id)}
              rowClassName={getRowClassName}
              pagination={{
                total: totalSize,
                showSizeChanger: false,
                onChange: handlePagination,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Catalogue;
